"use client";

import { useEffect, useState } from "react";
import { Minus, Square, Copy, X } from "lucide-react";
import { BrandIcon } from "@/components/brand";
import { getDesktopApi } from "@/lib/desktop";
import { cn } from "@/lib/cn";

/**
 * Custom title bar for the Electron shell. Renders nothing in the browser.
 *
 * Drag region: the bar itself uses `-webkit-app-region: drag` so the user can
 * drag the window from any empty area. Buttons opt out with `no-drag` so they
 * still receive clicks.
 *
 * Layout: position: fixed at the top. globals.css applies a 32px body padding
 * and shrinks `h-screen`/`min-h-screen` calculations whenever <html> has the
 * `desktop-shell` class (which we add on mount).
 */
export function DesktopTitleBar() {
  const [enabled, setEnabled] = useState(false);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    const api = getDesktopApi();
    if (!api?.windowMinimize) return;

    setEnabled(true);
    document.documentElement.classList.add("desktop-shell");

    let cancelled = false;
    api.windowIsMaximized?.().then((m) => {
      if (!cancelled) setMaximized(m);
    });

    const off = api.onWindowState?.((state) => {
      if (!cancelled) setMaximized(state.maximized);
    });

    return () => {
      cancelled = true;
      off?.();
      document.documentElement.classList.remove("desktop-shell");
    };
  }, []);

  if (!enabled) return null;

  const api = getDesktopApi();
  // The maximize button toggles between maximize and restore. The "restore"
  // glyph from lucide is `Copy` (two stacked squares) which is the standard
  // Windows convention.
  const MaxIcon = maximized ? Copy : Square;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] h-8 flex items-center bg-bg-base border-b border-line-subtle select-none"
      style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
    >
      <div className="flex items-center gap-1.5 px-3 text-fg-muted">
        <BrandIcon size={14} />
        <span className="text-[11px] tracking-wide">FixControl</span>
      </div>
      <div className="flex-1" />
      <div
        className="flex h-full"
        style={{ WebkitAppRegion: "no-drag" } as React.CSSProperties}
      >
        <TitleBarButton onClick={() => api?.windowMinimize?.()} aria-label="Minimize">
          <Minus size={12} />
        </TitleBarButton>
        <TitleBarButton onClick={() => api?.windowMaximizeToggle?.()} aria-label={maximized ? "Restore" : "Maximize"}>
          <MaxIcon size={11} />
        </TitleBarButton>
        <TitleBarButton onClick={() => api?.windowClose?.()} aria-label="Close" variant="close">
          <X size={13} />
        </TitleBarButton>
      </div>
    </div>
  );
}

function TitleBarButton({
  children, onClick, variant, ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "close";
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-full w-11 grid place-items-center text-fg-muted transition-colors",
        variant === "close"
          ? "hover:bg-status-critical hover:text-white"
          : "hover:bg-bg-hover hover:text-fg",
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
